import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { debounce } from 'lodash';
import { toaster } from '@saleshandy/design-system';
import { User } from '@saleshandy/icons';
import { useDispatch, useSelector } from 'react-redux';

import {
  getContactsListForManualCallDialingRequest,
  attachProspectToCallRequest,
} from '../../../../../components/dialer/extra-actions';
import {
  resetGetContactsListForManualCallDialingRequest,
  resetProspectMinimalListResponse,
  resetAttachProspectToCallRequest,
} from '../../../../../components/dialer/dialer-slice';
import { RootState } from '../../../../../store/root-reducer';
import { ExceptionTypes } from '../../../../types';
import { executeOnRequestStatus, getIsRequestPending } from '../../../../utils';
import Select from '../../../../design-system/components/select/select';
import Checkbox from '../../../../design-system/components/atoms/checkbox';
import { Placement } from '../../../../design-system/components/overlay';
import { ExtendedOption } from '../../../../design-system/components/transfer/transfer';
import { ProspectOption } from '../../types';
import { useInfiniteScroll } from '../../../../hooks/use-infinite-scroll';

import './link-number-to-prospect-modal.scss';
import Modal from '../../../../design-system/components/atoms/modal';

type ProspectSelectOption = ExtendedOption & {
  key: string;
  label: string;
  subtitle?: string;
};

export type LinkNumberToProspectModalProps = {
  show: boolean;
  onClose: () => void;
  /** E.164 or display phone being linked (shown as context). */
  callerPhone: string;
  /** Dialer call log id — required to attach the prospect to the correct call. */
  callId: number;
  onSubmit?: (payload: {
    contactId: number;
    markAsPrimary: boolean;
  }) => Promise<void> | void;
};

const formatProspectName = (prospect: ProspectOption): string => {
  const name =
    `${prospect?.firstName?.trim() || ''} ${
      prospect?.lastName?.trim() || ''
    }`.trim() || 'Unknown prospect';
  return name;
};

const LinkNumberToProspectModal: React.FC<LinkNumberToProspectModalProps> = ({
  show,
  onClose,
  callerPhone,
  callId,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const getContactsListForManualCallDialingRequestStatus = useSelector(
    (state: RootState) =>
      state.dialer.getContactsListForManualCallDialingRequest.status,
  );
  const prospectMinimalListResponse = useSelector(
    (state: RootState) => state.dialer.prospectMinimalListResponse,
  );
  const attachProspectToCallRequestStatus = useSelector(
    (state: RootState) => state.dialer.attachProspectToCallRequest.status,
  );

  const [prospects, setProspects] = useState<ProspectOption[]>([]);
  const [requestOptions, setRequestOptions] = useState({
    search: '',
    pageNum: 1,
    pageSize: 25,
    totalElements: 0,
  });
  const [selectedOptionKey, setSelectedOptionKey] = useState<
    string | undefined
  >(undefined);
  const [markAsPrimary, setMarkAsPrimary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const contactsListCancelTokenRef = useRef<ReturnType<
    typeof axios.CancelToken.source
  > | null>(null);
  const lastSearchTermRef = useRef<string>('');

  /** Ref for the dropdown's scrollable list — used by useInfiniteScroll. */
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  /**
   * Must live inside Modal children so portaled Select overlay stays under `.modal-dialog`.
   * If this ref wraps the Modal, RB portals the dialog away and the overlay mounts outside
   * the modal — `enforceFocus` then steals focus from the search input.
   */
  const modalContainerRef = useRef<HTMLDivElement | null>(null);

  const dispatchGetContactsList = useCallback(
    (payload: { search?: string; pageNum: number; pageSize: number }) => {
      if (contactsListCancelTokenRef.current) {
        contactsListCancelTokenRef.current.cancel({
          type: ExceptionTypes.Silent,
          message: 'LINK_NUMBER_PROSPECT_LIST_CANCELLED',
        } as any);
        contactsListCancelTokenRef.current = null;
      }
      contactsListCancelTokenRef.current = axios.CancelToken.source();
      const cancelToken = contactsListCancelTokenRef.current.token;
      dispatch(
        getContactsListForManualCallDialingRequest({
          ...payload,
          cancelToken,
        }),
      );
    },
    [dispatch],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        const trimmed = term?.trim();
        if (trimmed === lastSearchTermRef?.current) return;
        lastSearchTermRef.current = trimmed;
        setProspects([]);
        setRequestOptions((prev) => ({
          ...prev,
          search: trimmed,
          pageNum: 1,
          totalElements: 0,
        }));
        dispatchGetContactsList({
          search: trimmed,
          pageNum: 1,
          pageSize: requestOptions.pageSize,
        });
      }, 400),
    [dispatchGetContactsList, requestOptions.pageSize],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    if (!show) return;
    setSelectedOptionKey(undefined);
    setMarkAsPrimary(true);
    setSubmitError('');
    lastSearchTermRef.current = '';
    setProspects([]);
    setRequestOptions((prev) => ({
      ...prev,
      search: '',
      pageNum: 1,
      totalElements: 0,
    }));
    dispatchGetContactsList({ search: '', pageNum: 1, pageSize: 25 });
  }, [show, dispatchGetContactsList]);

  useEffect(() => {
    if (!show) {
      if (contactsListCancelTokenRef.current) {
        contactsListCancelTokenRef.current.cancel({
          type: ExceptionTypes.Silent,
          message: 'LINK_NUMBER_PROSPECT_LIST_CANCELLED',
        } as any);
        contactsListCancelTokenRef.current = null;
      }
      dispatch(resetGetContactsListForManualCallDialingRequest());
      dispatch(resetProspectMinimalListResponse());
      dispatch(resetAttachProspectToCallRequest());
      setSubmitError('');
    }
  }, [show, dispatch]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getContactsListForManualCallDialingRequestStatus,
      onSuccess: () => {
        const newResults = prospectMinimalListResponse?.results || [];
        if (prospectMinimalListResponse?.pageNum === 1) {
          setProspects(newResults);
        } else {
          setProspects((prev) => [...prev, ...newResults]);
        }
        setRequestOptions((prev) => ({
          ...prev,
          totalElements: prospectMinimalListResponse?.totalCount ?? 0,
        }));
      },
      onFailed: () => {
        setProspects([]);
      },
    });
  }, [
    getContactsListForManualCallDialingRequestStatus,
    prospectMinimalListResponse,
  ]);

  // Sync isSubmitting with attach request pending state
  useEffect(() => {
    if (getIsRequestPending(attachProspectToCallRequestStatus)) {
      setIsSubmitting(true);
      setSubmitError('');
    }
  }, [attachProspectToCallRequestStatus]);

  // Handle attach-to-call success/failure
  useEffect(() => {
    executeOnRequestStatus({
      status: attachProspectToCallRequestStatus,
      onSuccess: () => {
        setIsSubmitting(false);
        toaster.success('Prospect linked successfully');
        onSubmit?.({
          contactId: Number(selectedOptionKey),
          markAsPrimary,
        });
        onClose();
      },
      onFailed: () => {
        setIsSubmitting(false);
        setSubmitError('Failed to link prospect. Please try again.');
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachProspectToCallRequestStatus]);

  const selectOptions: ProspectSelectOption[] = useMemo(
    () =>
      prospects.map((p) => {
        const id = String(p.id ?? '');
        const name = formatProspectName(p);
        const subtitle =
          (p as ProspectOption & { email?: string }).email?.trim() || '';
        return {
          key: id,
          label: name,
          subtitle,
        };
      }),
    [prospects],
  );

  /** Keeps search input value in sync (`transfer.tsx` needs `filterOption` or `optionFilterProp`). Filters the current API page by label/email — server still drives `prospects` via debounced search. */
  const customFilterProspectOptions = useCallback(
    (opts: ProspectSelectOption[], search: string) => {
      const q = search?.trim().toLowerCase();
      if (!q) {
        return opts;
      }
      return opts.filter((o) => {
        const label = String(o.label ?? '').toLowerCase();
        const sub = String(o.subtitle ?? '').toLowerCase();
        return label.includes(q) || sub.includes(q);
      });
    },
    [],
  );

  const isListLoading = getIsRequestPending(
    getContactsListForManualCallDialingRequestStatus,
  );

  const hasMore = prospects.length < requestOptions.totalElements;

  const handleLoadMore = useCallback(() => {
    if (isListLoading || !hasMore) return;
    const nextPage = requestOptions.pageNum + 1;
    setRequestOptions((prev) => ({ ...prev, pageNum: nextPage }));
    dispatchGetContactsList({
      search: requestOptions.search,
      pageNum: nextPage,
      pageSize: requestOptions.pageSize,
    });
  }, [isListLoading, hasMore, requestOptions, dispatchGetContactsList]);

  useInfiniteScroll({
    scrollContainerRef,
    hasMore,
    onLoadMore: handleLoadMore,
    isLoadingMore: isListLoading,
    enabled: show,
    rebindKey: show ? 1 : 0,
  });

  const handleModalSubmit = useCallback(() => {
    if (!selectedOptionKey) return;
    dispatch(
      attachProspectToCallRequest({
        prospectId: Number(selectedOptionKey),
        callId,
        markAsPrimary,
      }),
    );
  }, [selectedOptionKey, callId, markAsPrimary, dispatch]);

  const titleContent = (
    <div className="bs-link-number-to-prospect-modal__title-row">
      <div className="bs-link-number-to-prospect-modal__title-icon">
        <User width={20} height={20} />
      </div>
      <div className="bs-link-number-to-prospect-modal__title-text">
        <span className="semibold-4 gray-txt-9">
          Link number to existing prospect
        </span>
        {callerPhone ? (
          <span className="regular-1 gray-txt-11">{callerPhone}</span>
        ) : null}
      </div>
    </div>
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      onHide={onClose}
      titleContent={titleContent}
      showCloseIcon
      isCustomCloseIcon
      onSubmit={handleModalSubmit}
      cancelButtonText="Cancel"
      submitButtonText="Add"
      isSubmitDisabled={!selectedOptionKey || isSubmitting}
      isSubmitLoading={isSubmitting}
      hideFooter={false}
      className="bs-link-number-to-prospect-modal"
    >
      <div
        ref={modalContainerRef}
        className="bs-link-number-to-prospect-modal__body"
      >
        <div className="semibold-2 gray-txt-9 bs-link-number-to-prospect-modal__field-label">
          Select a Prospect
        </div>
        <div id="link-number-prospect-select">
          <Select<ProspectSelectOption>
            className="bs-link-number-to-prospect-modal__select"
            placeholder="Search or Select Prospect"
            options={selectOptions}
            placement={Placement.Bottom}
            showSearch
            searchPlaceholder="Search or Select Prospect"
            isLoading={isListLoading}
            filterOption={() => true}
            // customFilterOption={customFilterProspectOptions}
            selectedOptionKey={selectedOptionKey}
            onSearch={(value) => debouncedSearch(value)}
            autoFocusSearch
            scrollContainerRef={scrollContainerRef}
            container={modalContainerRef}
            optionRenderer={(option) => (
              <div className="bs-link-number-to-prospect-modal__option">
                <span className="regular-2 gray-txt-9">{option.label}</span>
                {option.subtitle ? (
                  <span className="regular-1 gray-txt-11">
                    {option.subtitle}
                  </span>
                ) : null}
              </div>
            )}
            onChange={([option]) => {
              if (option?.key !== undefined && option?.key !== null) {
                setSelectedOptionKey(String(option.key));
              }
            }}
            selectedOptionRenderer={([option]) =>
              option ? (
                <span className="regular-2 gray-txt-9">{option.label}</span>
              ) : null
            }
          />
          {submitError ? (
            <p className="regular-1 red-txt-6 bs-link-number-to-prospect-modal__error">
              {submitError}
            </p>
          ) : null}
        </div>

        <div className="bs-link-number-to-prospect-modal__primary-block">
          <Checkbox
            checked={markAsPrimary}
            onChange={(checked) => setMarkAsPrimary(checked)}
            label={
              <span className="regular-2 gray-txt-9">Mark it as Primary</span>
            }
          />
          <p className="regular-1 gray-txt-11 bs-link-number-to-prospect-modal__primary-hint">
            When marked as primary, this number is preferred for outbound calls
            and display for this prospect.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default memo(LinkNumberToProspectModal);
